import React from 'react';
import { RewardShipment } from '@/types/missions';
import { Package, MapPin, Calendar, Sparkles, Gift, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface RewardSubmittedCardProps {
  shipment: RewardShipment;
  missionTitle: string;
}

export const RewardSubmittedCard: React.FC<RewardSubmittedCardProps> = ({
  shipment,
  missionTitle
}) => {
  const submittedDate = format(new Date(shipment.submitted_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-900/30 via-black/60 to-amber-900/30 border border-luxury-gold/30 shadow-2xl">
      {/* Shimmer effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
      
      {/* Content */}
      <div className="relative p-6 sm:p-8 space-y-6">
        {/* Icon and Badges */}
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="relative">
            <div className="w-20 h-20 bg-gradient-to-br from-luxury-gold/20 to-purple-500/20 rounded-full flex items-center justify-center animate-pulse-slow">
              <Gift className="w-10 h-10 text-luxury-gold" />
            </div>
            <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-black">
              <CheckCircle2 className="w-4 h-4 text-white" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-center">
            <span className="px-3 py-1 bg-luxury-gold/20 border border-luxury-gold/40 rounded-full text-luxury-gold text-xs font-semibold flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Dados Recebidos
            </span>
            <span className="px-3 py-1 bg-purple-500/20 border border-purple-500/40 rounded-full text-purple-300 text-xs font-semibold flex items-center gap-1">
              <Package className="w-3 h-3" />
              {shipment.sent ? 'Enviado' : 'Em Preparação'}
            </span>
          </div>
        </div>

        {/* Main Message */}
        <div className="text-center space-y-2">
          <h3 className="text-2xl font-bold text-white flex items-center justify-center gap-2">
            <Sparkles className="w-6 h-6 text-luxury-gold" />
            Brinde Confirmado!
            <Sparkles className="w-6 h-6 text-luxury-gold" />
          </h3>
          <p className="text-white/80 text-sm leading-relaxed max-w-md mx-auto">
            Recebemos seus dados de envio com sucesso! Em breve entraremos em contato com o código de rastreio para você acompanhar a entrega.
          </p>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        {/* Shipping Details */}
        <div className="space-y-4">
          <div className="bg-white/5 rounded-xl p-4 space-y-3">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-luxury-gold flex-shrink-0 mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="text-white/90 text-sm font-medium">Endereço de Entrega:</p>
                <p className="text-white/70 text-sm">
                  {shipment.address}, {shipment.number}
                  {shipment.complement && ` - ${shipment.complement}`}
                </p>
                <p className="text-white/70 text-sm">
                  CEP: {shipment.zip_code}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-luxury-gold flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-white/90 text-sm font-medium">Enviado em:</p>
                <p className="text-white/70 text-sm">{submittedDate}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        {/* Status Timeline */}
        <div className="space-y-3">
          <p className="text-white/90 text-sm font-medium flex items-center gap-2">
            <Package className="w-4 h-4 text-luxury-gold" />
            Status do Envio:
          </p>
          
          <div className="relative">
            <div className="flex items-center justify-between">
              {/* Step 1: Received */}
              <div className="flex flex-col items-center flex-1">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center border-2 border-black mb-2">
                  <CheckCircle2 className="w-4 h-4 text-white" />
                </div>
                <p className="text-white/80 text-xs font-medium text-center">Recebido</p>
              </div>

              {/* Connector */}
              <div className={`flex-1 h-0.5 ${shipment.sent ? 'bg-green-500' : 'bg-white/20'} -mt-8`} />

              {/* Step 2: Preparing */}
              <div className="flex flex-col items-center flex-1">
                <div className={`w-8 h-8 rounded-full ${!shipment.sent ? 'bg-purple-500 animate-pulse' : 'bg-green-500'} flex items-center justify-center border-2 border-black mb-2`}>
                  {shipment.sent ? (
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  ) : (
                    <Package className="w-4 h-4 text-white" />
                  )}
                </div>
                <p className="text-white/80 text-xs font-medium text-center">Preparando</p>
              </div>

              {/* Connector */}
              <div className={`flex-1 h-0.5 ${shipment.sent ? 'bg-green-500' : 'bg-white/20'} -mt-8`} />

              {/* Step 3: Shipped */}
              <div className="flex flex-col items-center flex-1">
                <div className={`w-8 h-8 rounded-full ${shipment.sent ? 'bg-green-500' : 'bg-white/20'} flex items-center justify-center border-2 border-black mb-2`}>
                  {shipment.sent && <CheckCircle2 className="w-4 h-4 text-white" />}
                </div>
                <p className={`text-xs font-medium text-center ${shipment.sent ? 'text-white/80' : 'text-white/40'}`}>Enviado</p>
              </div>

              {/* Connector */}
              <div className="flex-1 h-0.5 bg-white/20 -mt-8" />

              {/* Step 4: Delivered */}
              <div className="flex flex-col items-center flex-1">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center border-2 border-black mb-2" />
                <p className="text-white/40 text-xs font-medium text-center">Entregue</p>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        {/* Help Section */}
        <div className="text-center space-y-3">
          <p className="text-white/70 text-sm">💬 Precisa de ajuda?</p>
          <a
            href="/main/support"
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white text-sm font-medium rounded-xl transition-all duration-200 shadow-lg hover:shadow-purple-500/25"
          >
            Falar com Suporte
          </a>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-luxury-gold/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl" />
    </div>
  );
};
